import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import { mapGetters, mapState } from 'vuex';

import AddWebhookDialog from './AddWebhookDialog.vue';
import ConnectorEditor from './ConnectorEditor.vue';
import { DEFAULT_SENDER_TYPE, EDITABLE_FIELDS, READ_ONLY_METHODS } from '@/store/modules/mcpConnectors';

const I18N_PREFIX = 'iq_tools_tab.connectors';

/*
 * Connectors whose endpoint has no webhook host get a group of their own. The
 * backend needs a host to build a callable tool, so it reports these as
 * skipped; there is nothing to enable here, and the toggles stay disabled
 * rather than pretending the tool could run.
 */
const UNGROUPED_KEY = 'ungrouped';

export default {
  name: 'Connectors',
  components: {
    AddWebhookDialog,
    ConnectorEditor,
  },
  data() {
    return {
      I18N_PREFIX,
      loading: false,
      adding: false,
      search: '',
      openWebhooks: [],
      /*
       * Endpoint ids with a toggle request in flight. The switch is driven by
       * server state rather than by a local model, so without this a second
       * click during the round trip would send a second create for the same
       * endpoint and get a 409 back.
       */
      pendingEndpointIds: [],
      editingConnectorId: null,
      addDialogOpen: false,
    };
  },
  computed: {
    ...mapState({
      domains: state => state.settings.domains,
      endpoints: state => state.settings.endpoints,
    }),
    ...mapGetters('mcpConnectors', ['connectorsByEndpointId']),

    /*
     * Only webhooks that have been added, which means at least one of their
     * endpoints has a connector. There is no "added" flag on a webhook, and
     * this is the same thing: a webhook is on this page because something of
     * it is exposed. Everything else is offered by the add dialog instead of
     * padding the list with webhooks nobody has decided about.
     */
    webhooks() {
      const grouped = orderBy(this.domains, [domain => String(domain.name).toLowerCase()], ['asc'])
        .map(domain => this.buildGroup({
          key: `webhook-${domain.id}`,
          id: domain.id,
          name: domain.name,
          host: [domain.protocol, domain.host].filter(Boolean).join('://'),
          endpoints: this.endpointsOf(domain.id),
          available: true,
        }))
        .filter(group => group.connectorCount > 0);

      const orphans = this.endpointsWithoutDomain();
      const orphanGroup = this.buildGroup({
        key: UNGROUPED_KEY,
        id: null,
        name: this.$t(`${I18N_PREFIX}.ungrouped`),
        host: '',
        endpoints: orphans,
        available: false,
      });
      if (orphanGroup.connectorCount > 0) {
        grouped.push(orphanGroup);
      }

      return grouped;
    },

    /*
     * What the add dialog offers: the webhooks not on the page yet, each with
     * the endpoints that could be exposed.
     */
    addableWebhooks() {
      const added = new Set(this.webhooks.map(({ id }) => id));

      return orderBy(this.domains, [domain => String(domain.name).toLowerCase()], ['asc'])
        .filter(domain => !added.has(domain.id))
        .map(domain => ({
          id: domain.id,
          name: domain.name,
          host: [domain.protocol, domain.host].filter(Boolean).join('://'),
          endpoints: orderBy(
            this.endpointsOf(domain.id).map(endpoint => this.describeCall(endpoint)),
            [endpoint => endpoint.name.toLowerCase()],
            ['asc'],
          ),
        }));
    },

    /*
     * Search matches a webhook by its own name or by any of its endpoints, so
     * looking for an endpoint still shows the webhook it belongs to.
     */
    visibleWebhooks() {
      const term = this.search.trim().toLowerCase();
      if (!term) {
        return this.webhooks;
      }

      return this.webhooks.reduce((acc, webhook) => {
        if (webhook.name.toLowerCase().includes(term)) {
          acc.push(webhook);
          return acc;
        }
        const endpoints = webhook.endpoints.filter(endpoint => this.matches(endpoint, term));
        if (endpoints.length) {
          acc.push({ ...webhook, endpoints });
        }
        return acc;
      }, []);
    },

    enabledTotal() {
      return this.webhooks.reduce((total, webhook) => total + webhook.enabledCount, 0);
    },

    endpointTotal() {
      return this.webhooks.reduce((total, webhook) => total + webhook.endpoints.length, 0);
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        /*
         * The webhook and endpoint lists are the same ones the API settings
         * page reads, so this reuses that action instead of adding a second
         * way to fetch them.
         */
        await Promise.all([
          this.$store.dispatch('settings/getAPISettings'),
          this.$store.dispatch('mcpConnectors/listConnectors'),
        ]);
        this.openAllWebhooks();
      } catch (err) {
        this.notifyError(err, `${I18N_PREFIX}.errors.load`);
      } finally {
        this.loading = false;
      }
    },

    // Every group starts expanded: the list only holds webhooks someone added,
    // so there is nothing to scroll past before reaching what matters.
    openAllWebhooks() {
      this.openWebhooks = this.webhooks.map(({ key }) => key);
    },

    endpointsOf(domainId) {
      return this.endpoints.filter(endpoint => endpoint.webhook_id === domainId);
    },

    endpointsWithoutDomain() {
      const knownDomainIds = new Set(this.domains.map(({ id }) => id));
      return this.endpoints.filter(endpoint => !knownDomainIds.has(endpoint.webhook_id));
    },

    // Name, method and path as they are shown and as derivePurpose reads them.
    describeCall(endpoint) {
      return {
        id: endpoint.id,
        name: endpoint.name,
        method: String(endpoint.method || 'get').toUpperCase(),
        path: endpoint.path || '/',
      };
    },

    buildGroup({ key, id, name, host, endpoints, available }) {
      const rows = orderBy(endpoints, [endpoint => String(endpoint.name).toLowerCase()], ['asc'])
        .map(endpoint => this.describeEndpoint(endpoint, available));

      return {
        key,
        id,
        name,
        host,
        available,
        endpoints: rows,
        enabledCount: rows.filter(row => row.enabled).length,
        // How many endpoints have a connector at all, enabled or not. This is
        // what puts the webhook on the page.
        connectorCount: rows.filter(row => row.connector).length,
      };
    },

    /*
     * One endpoint as the row renders it: whether it is exposed, and whether
     * being exposed is actually enough for the backend to publish it as a tool.
     */
    describeEndpoint(endpoint, available) {
      const connector = this.connectorsByEndpointId[endpoint.id] || null;
      const enabled = Boolean(get(connector, 'enabled'));
      const call = this.describeCall(endpoint);
      const readOnly = READ_ONLY_METHODS.includes(call.method.toLowerCase());

      return {
        ...call,
        connector,
        connectorId: get(connector, 'id', null),
        toolName: get(connector, 'tool_name', ''),
        senderType: get(connector, 'sender_type', ''),
        enabled,
        // A write endpoint stays out of the tool list until allow_write is set,
        // so an enabled toggle on its own would overstate what is published.
        needsWriteAccess: enabled && !readOnly && !get(connector, 'allow_write', false),
        togglable: available,
        pending: this.pendingEndpointIds.includes(endpoint.id),
      };
    },

    matches(endpoint, term) {
      return [endpoint.name, endpoint.path, endpoint.method]
        .some(value => String(value || '').toLowerCase().includes(term));
    },

    /*
     * The sentence a model reads to decide whether to call the tool. Endpoints
     * carry no description, so this is a starting point built from what they do
     * carry; the editor is where it gets written properly.
     */
    derivePurpose(webhook, endpoint) {
      const target = `${endpoint.method} ${endpoint.path}`;
      return webhook.name
        ? `Calls ${endpoint.name} (${target}) on ${webhook.name}.`
        : `Calls ${endpoint.name} (${target}).`;
    },

    createConnector(webhook, endpoint) {
      return this.$store.dispatch('mcpConnectors/createConnector', {
        endpoint_id: endpoint.id,
        purpose: this.derivePurpose(webhook, endpoint),
        // tool_name is left out on purpose: the backend derives it from the
        // webhook and endpoint names, which is where uniqueness is known.
        sender_type: DEFAULT_SENDER_TYPE,
      });
    },

    async onToggle(webhook, endpoint, value) {
      if (endpoint.pending) {
        return;
      }
      this.pendingEndpointIds = [...this.pendingEndpointIds, endpoint.id];

      try {
        if (endpoint.connector) {
          // The whole connector goes on the wire rather than the one flag that
          // moved: a PUT here states what the connector is, not what changed.
          // Disabling still keeps the row, so the purpose and tool name written
          // for it survive and re-enabling stays a single click.
          await this.$store.dispatch('mcpConnectors/updateConnector', {
            id: endpoint.connector.id,
            body: { ...pick(endpoint.connector, EDITABLE_FIELDS), enabled: value },
          });
        } else if (value) {
          await this.createConnector(webhook, endpoint);
        }
      } catch (err) {
        this.notifyError(err, `${I18N_PREFIX}.errors.toggle`);
      } finally {
        this.pendingEndpointIds = this.pendingEndpointIds.filter(id => id !== endpoint.id);
      }
    },

    openAddDialog() {
      this.addDialogOpen = true;
    },

    closeAddDialog() {
      this.addDialogOpen = false;
    },

    /*
     * Adding a webhook is creating connectors for the endpoints picked in the
     * dialog: that is what puts it on the page, and it means nothing is added
     * that would not survive a reload.
     */
    async onAddWebhook({ webhook, endpoints }) {
      this.adding = true;
      try {
        const failures = (await Promise.all(
          endpoints.map(endpoint => this.createConnector(webhook, endpoint)
            .then(() => null)
            // One endpoint failing should not discard the ones that worked, so
            // the error is carried rather than thrown.
            .catch(err => err)),
        )).filter(Boolean);

        if (failures.length) {
          this.notifyError(failures[0], `${I18N_PREFIX}.errors.add`);
        }
        if (failures.length < endpoints.length) {
          this.openWebhooks = [...this.openWebhooks, `webhook-${webhook.id}`];
          this.closeAddDialog();
        }
      } finally {
        this.adding = false;
      }
    },

    /*
     * Removing a webhook from the page deletes its connectors. The webhook and
     * its endpoints are untouched: this page only decides what the AI may call.
     */
    async removeWebhook(webhook) {
      const connectorIds = webhook.endpoints
        .map(endpoint => endpoint.connectorId)
        .filter(Boolean);

      const confirmed = await this.$aiq.confirm(
        this.$t(`${I18N_PREFIX}.remove.title`),
        this.$t(`${I18N_PREFIX}.remove.content`),
      );
      if (!confirmed) {
        return;
      }

      const failures = (await Promise.all(
        connectorIds.map(id => this.$store.dispatch('mcpConnectors/deleteConnector', id)
          .then(() => null)
          .catch(err => err)),
      )).filter(Boolean);

      if (failures.length) {
        this.notifyError(failures[0], `${I18N_PREFIX}.errors.remove`);
      }
    },

    openEditor(endpoint) {
      if (!endpoint.connectorId) {
        return;
      }
      this.editingConnectorId = endpoint.connectorId;
    },

    closeEditor() {
      this.editingConnectorId = null;
      this.$store.dispatch('mcpConnectors/clearConnectorItem');
    },

    notifyError(err, fallbackKey) {
      this.$aiq.notify.error(
        get(err, 'response.data.message') || this.$t(fallbackKey),
      );
    },
  },
};
